import { DependencyList, useState } from "react";

const areKeysEqual = (left: DependencyList, right: DependencyList) =>
    left.length === right.length &&
    left.every((key, index) => Object.is(key, right[index]));

// Called with `keys` so React Compiler caches the result per `keys`, not per
// `compute` alone: a stable `compute` would otherwise return its first value forever.
const takeSnapshot = <T>(compute: () => T, keys: DependencyList) => ({
    keys,
    value: compute(),
});

/**
 * Like `useMemo`, but recomputes only when one of `keys` changes (by
 * `Object.is`), whatever `compute` reads. For values keyed by content (a hash
 * or a data-state string) rather than by the identity of their inputs, and
 * for keys that arrive as a caller's dependency list.
 *
 * Unlike `useMemo`, a changed key sets state during render, so every key must
 * keep its identity across re-renders: a fresh object, array or closure as a
 * key loops with "Too many re-renders".
 */
export const useMemoByKeys = <T>(compute: () => T, keys: DependencyList): T => {
    const [snapshot, setSnapshot] = useState(() => takeSnapshot(compute, keys));
    if (areKeysEqual(snapshot.keys, keys)) {
        return snapshot.value;
    }
    const next = takeSnapshot(compute, keys);
    setSnapshot(next);
    return next.value;
};
